import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight, Sun, Moon } from "lucide-react";
import { useContent, useLang } from "@/context/AppContext";
import { EASE } from "@/components/ui-bits";

const LINKS = [
  { href: "#about", key: "nav.about", id: "about" },
  { href: "#expertise", key: "nav.expertise", id: "expertise" },
  { href: "#cases", key: "nav.cases", id: "cases" },
  { href: "#experience", key: "nav.experience", id: "experience" },
  { href: "#certifications", key: "nav.certs", id: "certifications" },
];

export function LangSwitcher() {
  const { lang, setLang } = useLang();
  return (
    <div data-testid="language-switcher-toggle" className="flex items-center gap-1 font-mono text-xs font-bold">
      {["en", "id"].map((l, i) => (
        <span key={l} className="flex items-center">
          {i > 0 && <span className="mx-1 text-hairline">/</span>}
          <button
            onClick={() => setLang(l)}
            data-testid={`language-switch-${l}`}
            className={`px-0.5 py-1 uppercase tracking-widest transition-colors ${lang === l ? "text-vermilion" : "text-sand hover:text-bone"}`}
          >
            {l}
          </button>
        </span>
      ))}
    </div>
  );
}

export function ThemeToggle() {
  const [theme, setTheme] = useState(() => localStorage.getItem("theme") || "light");

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem("theme", theme);
  }, [theme]);

  const isDark = theme === "dark";

  return (
    <button
      onClick={() => setTheme(isDark ? "light" : "dark")}
      data-testid="theme-toggle-button"
      aria-label={isDark ? "Switch to light mode" : "Switch to night mode"}
      title={isDark ? "Mode Terang" : "Mode Malam"}
      className="flex h-9 w-9 items-center justify-center border border-hairline text-sand transition-colors hover:border-vermilion hover:text-vermilion"
    >
      {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
    </button>
  );
}

export default function Navbar() {
  const { t } = useLang();
  const { content } = useContent();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const whatsapp = content?.settings?.whatsapp_link || "#contact";

  return (
    <header
      data-testid="navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${scrolled || open ? "border-b border-hairline bg-obsidian/90 backdrop-blur-md" : "bg-transparent"}`}
    >
      <nav className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-10 px-4 sm:px-8 lg:h-[76px] lg:px-16">
        <a href="#top" data-testid="navbar-brand-logo" className="flex items-baseline gap-1">
          <span className="whitespace-nowrap font-display text-xl font-semibold tracking-tight text-bone">
            Ryan Hidayat
          </span>
          <span className="font-display text-xl italic text-vermilion">.</span>
        </a>
        <div className="hidden items-center gap-8 lg:flex">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`navbar-link-${l.id}`}
              className="group relative whitespace-nowrap font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-sand transition-colors hover:text-bone"
            >
              {t(l.key)}
              <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-vermilion transition-all duration-300 group-hover:w-full" />
            </a>
          ))}
        </div>
        <div className="hidden items-center gap-4 lg:flex">
          <LangSwitcher />
          <ThemeToggle />
          <a
            href={whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="navbar-cta-discuss-button"
            className="group inline-flex items-center gap-2 bg-ink px-5 py-2.5 font-mono text-[11px] font-bold uppercase tracking-[0.15em] text-paper transition-colors hover:bg-vermilion"
          >
            {t("navCta")}
            <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </div>
        <div className="flex items-center gap-3 lg:hidden">
          <LangSwitcher />
          <ThemeToggle />
          <button
            data-testid="navbar-mobile-toggle"
            onClick={() => setOpen(!open)}
            className="p-2 text-bone"
            aria-label="Menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: EASE }}
            className="overflow-hidden border-t border-hairline bg-obsidian/95 backdrop-blur-md lg:hidden"
          >
            <div className="space-y-1 px-4 py-5">
              {LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  data-testid={`navbar-mobile-link-${l.id}`}
                  className="block border-b border-hairline/60 px-2 py-3.5 font-mono text-xs font-semibold uppercase tracking-[0.18em] text-sand hover:text-bone"
                >
                  {t(l.key)}
                </a>
              ))}
              <a
                href={whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="navbar-mobile-cta-button"
                className="mt-4 flex items-center justify-center gap-2 bg-ink px-4 py-3.5 font-mono text-xs font-bold uppercase tracking-[0.15em] text-paper"
              >
                {t("navCta")}
                <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
